import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { HiChevronLeft, HiChevronRight, HiClock } from "react-icons/hi";
import { CombinedItem } from "../../../pages/Projects/roadmapsAndProjects.types";
import { resetRoadmap, setSingleRoadmap } from "../../../redux/slices/roadmap";
import { resetTimelineState } from "../../../redux/slices/timeline";
import RecentWorkUnitsSkeleton from "./RecentWorkUnitsSkeleton";
import ProjectBox from "../../../pages/Projects/sections/Project/sections/ProjectBoxComponent/ProjectBox"; // Deep import, might want to move this later
import "./RecentWorkUnits.css";
import WorkUnitThumbnail from "../components/WorkUnitsCard";
import AddUpdateProjectModal from "../../../pages/Projects/sections/AddUpdateProjectModal";
import CreateAdvancedEpicModal from "../../AdvancedEpic/CreateAdvancedEpicModal";
import Portal from "../../../components/Portal";
import { useOrganizationService } from "../../../services";
import { WorkUnitsCardSkeleton } from "../components/WorkUnitsCardSkeleton";
import { PinIcon } from "lucide-react";
import WorkUnitCreateSkeleton from "../components/WorkUnitsCreateSkeleton";
import PinSelectionModal from "../components/PinSelectionModal";
import { ReduxStateType } from "../../../redux/store";

const RecentWorkUnits = ({
  handleProjectClick,
  loading = false,
}: {
  handleProjectClick: (project?: any) => void;
  loading?: boolean;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fetchRoadmapsAndProjects, fetchPinnedItems } =
    useOrganizationService();

  // Get current workItems filters from Redux to preserve them when reloading
  const {
    workItemsSearchTerm,
    workItemsSearchType,
    workItemsSortBy,
    workItemsSourceFilter,
    workItemsQuarterFilter = [],
    workItemsPinnedFilter = false,
  } = useSelector((state: any) => state.organization);

  // Edit modals state
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showRoadmapModal, setShowRoadmapModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>({});
  const [selectedRoadmap, setSelectedRoadmap] = useState<any>({});
  const { pinnedProjects = [], pinnedProjectsIds } = useSelector(
    (state: ReduxStateType) => state.organization,
  );

  const [showPinModal, setShowPinModal] = useState(false);

  // Refs and state for scroll functionality
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const showSkeleton = loading;

  // Handle click on recent item - navigate to roadmap or project page
  const handleRecentItemClick = (item: any) => {
    if (item.type === "roadmap") {
      // Reset states for Epic Advance to ensure fresh data is fetched (matching RoadmapThumbnail behavior)
      dispatch(resetTimelineState());
      dispatch(resetRoadmap());
      dispatch(setSingleRoadmap({} as any));
      navigate(`/roadmaps/${item.id}`);
    } else {
      // For projects, use the existing handler
      handleProjectClick(item);
    }
  };

  // Check scroll position and update navigation button states
  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Handle vertical scroll to horizontal scroll conversion and inject CSS
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener("scroll", updateScrollButtons);

    // Initial button state check
    updateScrollButtons();

    return () => {
      scrollContainer.removeEventListener("scroll", updateScrollButtons);
    };
  }, [pinnedProjects]);

  // Manual scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const cardWidth = scrollContainerRef.current.clientWidth * 0.28; // 28% width per card
      scrollContainerRef.current.scrollBy({
        left: -cardWidth,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const cardWidth = scrollContainerRef.current.clientWidth * 0.28; // 28% width per card
      scrollContainerRef.current.scrollBy({
        left: cardWidth,
        behavior: "smooth",
      });
    }
  };

  const handleEdit = (data: CombinedItem) => {
    if (data.type === "roadmap") {
      // Epic Advance - use CreateRoadMapsModal
      setSelectedRoadmap({ ...data });
      setShowRoadmapModal(true);
    } else {
      // Normal project - use AddUpdateProjectModal
      const projectData = {
        ...data,
        project_id: data.id,
        project_name: data.name,
        project_description: data.description,
        project_color: data.color,
      };
      setSelectedProject(projectData);
      setShowProjectModal(true);
    }
  };

  const handleRoadmapModalClose = (val: boolean) => {
    setShowRoadmapModal(val);
    if (!val) {
      setSelectedRoadmap({});
      dispatch(setSingleRoadmap({}));
    }
  };

  const handleProjectModalClose = (val: boolean) => {
    setShowProjectModal(val);
    if (!val) {
      setSelectedProject({});
    }
  };

  return (
    <div className={`flex flex-col pl-6`}>
      {
        <div className="flex justify-between items-center py-4  z-[1]">
          <div className="flex items-center gap-2">
            <PinIcon className="w-5 h-5 text-slate-400 dark:text-slate-500" />
            <h2 className="text-base font-semibold text-light-text-primary dark:text-white">
              Pinned Items
            </h2>
          </div>
          <div className="flex items-center gap-2 pr-6">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Scroll to explore
            </span>
            <button
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={`p-2 rounded-full transition-colors ${
                canScrollLeft
                  ? "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
              }`}
            >
              <HiChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={`p-2 rounded-full transition-colors ${
                canScrollRight
                  ? "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
              }`}
            >
              <HiChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      }

      <div className="relative">
        {/* Left gradient overlay */}
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white dark:from-gray-900 to-transparent z-10 pointer-events-none" />
        )}

        {/* Right gradient overlay */}
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-gray-900 to-transparent z-10 pointer-events-none" />
        )}

        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto space-x-4 recent-projects-scroll-container"
        >
          {showSkeleton ? (
            <>
              {[...Array(5)].map((_, index) => (
                <div
                  key={`loading-skeleton-${index}`}
                  className={`group flex-shrink-0 max-w-[350px] relative h-full `}
                  style={{ width: "28%" }}
                >
                  <WorkUnitsCardSkeleton minimized={true} />
                </div>
              ))}
            </>
          ) : (
            <>
              {pinnedProjects.length ? (
                <>
                  {pinnedProjects.map((proj: CombinedItem, index: number) => (
                    <div
                      key={proj.id}
                      className={`group flex-shrink-0 max-w-[350px] relative h-full`}
                      style={{ width: "28%" }}
                    >
                      <WorkUnitThumbnail
                        minimzed={true}
                        data={proj}
                        onClick={() => handleRecentItemClick(proj)}
                        onEdit={(data: any) => handleEdit(data)}
                      />
                    </div>
                  ))}
                  <div
                    className={`group flex-shrink-0 max-w-[350px] relative h-full`}
                    style={{ width: "28%" }}
                  >
                    <WorkUnitCreateSkeleton
                      onClick={() => setShowPinModal(true)}
                    />
                  </div>
                </>
              ) : (
                <div
                  className={`group flex-shrink-0 max-w-[350px] relative h-full`}
                  style={{ width: "28%" }}
                >
                  <WorkUnitCreateSkeleton
                    onClick={() => setShowPinModal(true)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Project Modal */}
      {showProjectModal && (
        <Portal>
          <AddUpdateProjectModal
            isVisible={showProjectModal}
            setShowModal={handleProjectModalClose}
            project={selectedProject}
            onProjectUpdate={() => {
              // Only refresh when there's an actual update - preserve filters
              fetchRoadmapsAndProjects(
                true,
                undefined,
                workItemsSearchTerm,
                workItemsSearchType,
                workItemsSortBy,
                workItemsSourceFilter,
                workItemsQuarterFilter,
                workItemsPinnedFilter,
              );
              fetchPinnedItems();
            }}
          />
        </Portal>
      )}

      {/* Edit Roadmap Modal */}
      {showRoadmapModal && (
        <Portal>
          <CreateAdvancedEpicModal
            isVisible={showRoadmapModal}
            setShowModal={handleRoadmapModalClose}
            roadmap={selectedRoadmap}
            setSelectedRoadmap={async () => {
              fetchRoadmapsAndProjects(
                true,
                undefined,
                workItemsSearchTerm,
                workItemsSearchType,
                workItemsSortBy,
                workItemsSourceFilter,
                workItemsQuarterFilter,
                workItemsPinnedFilter,
              );
              fetchPinnedItems();
            }}
          />
        </Portal>
      )}

      {/* Pin Selection Modal */}
      {showPinModal && (
        <Portal>
          <PinSelectionModal
            isVisible={showPinModal}
            onClose={() => setShowPinModal(false)}
            onSuccess={() => {
              fetchPinnedItems();
              // Trigger refresh of list view if needed
              fetchRoadmapsAndProjects(
                true,
                undefined,
                workItemsSearchTerm,
                workItemsSearchType,
                workItemsSortBy,
                workItemsSourceFilter,
                workItemsQuarterFilter,
                workItemsPinnedFilter,
              );
            }}
          />
        </Portal>
      )}
    </div>
  );
};

export default RecentWorkUnits;
